import React from 'react';
import { ShieldCheckIcon } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/Button';
import { Tag } from '../ui/Tag';
import { useMarketplace } from '../../contexts/MarketplaceContext';
import { baht, bookingFee, formatDate } from '../../utils/format';
import type { Offer, Role } from '../../types/marketplace';
interface OfferCardProps {
  offer: Offer;
  role: Role;
}
export function OfferCard({
  offer,
  role
}: OfferCardProps) {
  const {
    matches,
    getEvent,
    getPerformer,
    respondOffer
  } = useMarketplace();
  const match = matches.find(m => m.id === offer.matchId);
  const event = match ? getEvent(match.eventId) : undefined;
  const performer = match ? getPerformer(match.performerId) : undefined;
  const slot = event?.slots.find(s => s.id === offer.slotId);
  if (!event || !performer) return null;
  const subtotal = offer.fee + offer.travel;
  const fee = bookingFee(subtotal);
  return <div className="w-full max-w-[360px] overflow-hidden rounded-md border border-line bg-surface" data-id="element-153" data-mp="ml6ynqzggaki">
      <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3" data-id="element-154" data-mp="2dbi6lu8eo2ve">
        <p className="font-bold" data-id="element-155" data-mp="289sra2e5h31i">Booking offer</p>
        {offer.status === 'pending' && <Tag tone="brand" data-id="element-156" data-mp="24abkn5mzlzur">Awaiting reply</Tag>}
        {offer.status === 'accepted' && <Tag tone="success" data-id="element-157" data-mp="1zb7dekjmic72">Accepted</Tag>}
        {offer.status === 'declined' && <Tag data-id="element-158" data-mp="2w0oo5dxgqofm">Declined</Tag>}
      </div>
      <div className="px-4 py-3" data-id="element-159" data-mp="2vzn62pi151oe">
        <p className="text-[15px] font-bold" data-id="element-160" data-mp="2w2sr9tnjfjeu">{event.name}</p>
        <p className="text-sm text-ink-2" data-id="element-161" data-mp="2s05xvob7xqfj">
          {formatDate(event.date)} · {slot?.kind ?? 'Act'} · {offer.setLength} min · arrive {offer.arrival}
        </p>
        <dl className="mt-3 space-y-1.5 text-sm" data-id="element-162" data-mp="2i9jmy7eg5o36">
          <Row label="Performance fee" value={baht(offer.fee)} data-id="element-163" data-mp="2i8s2nsqavcs7" />
          <Row label="Travel" value={baht(offer.travel)} data-id="element-164" data-mp="3fp76pcnyr3lc" />
          {role === 'organiser' ? <>
              <Row label="Encorza booking fee (6%)" value={baht(fee)} data-id="element-165" data-mp="3awxn0edfyzri" />
              <div className="border-t border-line pt-1.5" data-id="element-166" data-mp="3tuherblx4lnu">
                <Row label="You pay" value={baht(subtotal + fee)} strong data-id="element-167" data-mp="3tuzeii7shi0i" />
              </div>
            </> : <div className="border-t border-line pt-1.5" data-id="element-168" data-mp="3l1vufr2n26ww">
              <Row label="You receive" value={baht(subtotal)} strong data-id="element-169" data-mp="3l1e1p4f6qatq" />
            </div>}
        </dl>
        <p className="mt-3 flex items-start gap-2 text-[13px] leading-snug text-ink-3" data-id="element-170" data-mp="3p1oaeoud3fca">
          <ShieldCheckIcon className="mt-px h-4 w-4 shrink-0" data-id="element-171" data-mp="3b252nx5gq60i" />
          Protected payment: held by Encorza and released 24h after the gig.
        </p>
      </div>
      {offer.status === 'pending' && role === 'performer' && <div className="flex gap-2 border-t border-line p-3" data-id="element-172" data-mp="325e9bsiap59m">
          <Button variant="secondary" size="sm" className="flex-1" onClick={() => respondOffer(offer.id, false)} data-id="element-173" data-mp="365eoxgh4rmi9">
            Decline
          </Button>
          <Button variant="accent" size="sm" className="flex-1" onClick={() => {
        respondOffer(offer.id, true);
        toast.success('Gig confirmed', {
          description: `${event.name} is in your calendar.`
        });
      }} data-id="element-174" data-mp="14d07eiii0k2d">
            Accept offer
          </Button>
        </div>}
      {offer.status === 'pending' && role === 'organiser' && <p className="border-t border-line px-4 py-3 text-sm text-ink-2" data-id="element-175" data-mp="2ttchtjbkpfqm">Waiting for {performer.name} to respond.</p>}
    </div>;
}
function Row({
  label,
  value,
  strong
}: {
  label: string;
  value: string;
  strong?: boolean;
}) {
  return <div className="flex items-center justify-between gap-3" data-id="element-176" data-mp="33mjlmzqo0nol">
      <dt className={strong ? 'font-bold' : 'text-ink-2'} data-id="element-177" data-mp="3hiorekatbptg">{label}</dt>
      <dd className={`tabular ${strong ? 'text-base font-bold' : 'font-semibold'}`} data-id="element-178" data-mp="3ckdhyaofjhbr">{value}</dd>
    </div>;
}